import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { getProp, type PropDef, type PropSlot } from '@/game/props'

interface EmberProps3DProps {
  equippedProps: string[]
  bodyScale?: number
  reducedMotion?: boolean
  slots?: PropSlot[]
  depth?: number
}

const SLOT_ORDER: PropSlot[] = ['back', 'head', 'hair', 'face', 'hand', 'accessory']

function HeadProp({ prop, r }: { prop: PropDef; r: number }) {
  const { color, secondary, glow } = prop.palette
  if (prop.id === 'wizard-hat' || prop.id === 'ice-crown') {
    return (
      <group position={[0, r * 1.34, 0.02]}>
        <mesh castShadow>
          <coneGeometry args={[r * 0.48, r * 0.94, 20]} />
          <meshStandardMaterial color={color} emissive={glow} emissiveIntensity={0.25} roughness={0.5} />
        </mesh>
        <mesh position={[0, r * 0.54, 0]}>
          <icosahedronGeometry args={[r * 0.16, 0]} />
          <meshStandardMaterial color={secondary} emissive={secondary} emissiveIntensity={0.6} />
        </mesh>
      </group>
    )
  }
  if (prop.id === 'viking-helmet') {
    return (
      <group position={[0, r * 1.04, 0.02]}>
        <mesh castShadow>
          <sphereGeometry args={[r * 0.54, 20, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial color={color} metalness={0.4} roughness={0.5} />
        </mesh>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * r * 0.55, r * 0.18, 0]} rotation={[0, 0, s * -0.5]}>
            <coneGeometry args={[r * 0.12, r * 0.55, 10]} />
            <meshStandardMaterial color={secondary} roughness={0.4} />
          </mesh>
        ))}
      </group>
    )
  }
  if (prop.id === 'gamer-headphones') {
    return (
      <group position={[0, r * 0.45, r * 0.12]}>
        <mesh position={[0, r * 0.42, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[r * 0.62, r * 0.07, 8, 20, Math.PI]} />
          <meshStandardMaterial color={color} roughness={0.4} />
        </mesh>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * r * 0.66, 0, 0]}>
            <cylinderGeometry args={[r * 0.2, r * 0.2, r * 0.24, 16]} />
            <meshStandardMaterial color={color} emissive={glow} emissiveIntensity={0.5} roughness={0.4} />
          </mesh>
        ))}
      </group>
    )
  }
  return (
    <group position={[0, r * 1.08, 0.02]}>
      <mesh castShadow>
        <cylinderGeometry args={[r * 0.46, r * 0.58, r * 0.28, 20]} />
        <meshStandardMaterial color={color} emissive={glow} emissiveIntensity={0.2} roughness={0.5} />
      </mesh>
    </group>
  )
}

function HairProp({ prop, r }: { prop: PropDef; r: number }) {
  const { color, secondary, glow } = prop.palette
  if (prop.id === 'spiky-hair') {
    return (
      <group position={[0, r * 1.02, 0.02]}>
        {Array.from({ length: 6 }, (_, i) => {
          const a = (i / 6) * Math.PI * 2
          return (
            <mesh key={i} position={[Math.cos(a) * r * 0.3, r * 0.1, Math.sin(a) * r * 0.3]} rotation={[Math.cos(a) * 0.5, 0, -Math.sin(a) * 0.5]}>
              <coneGeometry args={[r * 0.13, r * 0.5, 6]} />
              <meshStandardMaterial color={color} emissive={secondary} emissiveIntensity={0.15} roughness={0.5} />
            </mesh>
          )
        })}
      </group>
    )
  }
  return (
    <mesh position={[0, r * 0.82, 0.02]} castShadow>
      <sphereGeometry args={[r * 0.58, 16, 14, 0, Math.PI * 2, 0, Math.PI * 0.62]} />
      <meshStandardMaterial color={color} emissive={glow} emissiveIntensity={0.12} roughness={0.85} />
    </mesh>
  )
}

function FaceProp({ prop, r }: { prop: PropDef; r: number }) {
  const { color, secondary, glow } = prop.palette
  const z = r * 1.08
  if (prop.id === 'cool-glasses' || prop.id === 'hipster-glasses') {
    return (
      <group position={[0, r * 0.14, z * 0.92]}>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * r * 0.24, 0, 0]}>
            <torusGeometry args={[r * 0.17, r * 0.04, 8, 20]} />
            <meshStandardMaterial color={color} metalness={0.3} roughness={0.4} />
          </mesh>
        ))}
        <mesh>
          <boxGeometry args={[r * 0.16, r * 0.035, r * 0.035]} />
          <meshStandardMaterial color={color} />
        </mesh>
      </group>
    )
  }
  if (prop.id === 'fancy-moustache') {
    return (
      <group position={[0, -r * 0.14, z]}>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * r * 0.16, 0, 0]} rotation={[0, 0, s * -0.6]}>
            <capsuleGeometry args={[r * 0.05, r * 0.2, 4, 8]} />
            <meshStandardMaterial color={color} roughness={0.7} />
          </mesh>
        ))}
      </group>
    )
  }
  if (prop.id === 'pirate-beard') {
    return (
      <mesh position={[0, -r * 0.34, z * 0.8]} castShadow>
        <coneGeometry args={[r * 0.4, r * 0.6, 12]} />
        <meshStandardMaterial color={color} emissive={glow} emissiveIntensity={0.08} roughness={0.85} />
      </mesh>
    )
  }
  return (
    <mesh position={[0, r * 0.1, z]}>
      <boxGeometry args={[r * 0.5, r * 0.14, r * 0.06]} />
      <meshStandardMaterial color={color} emissive={secondary} emissiveIntensity={0.2} />
    </mesh>
  )
}

function HandProp({ prop, r }: { prop: PropDef; r: number }) {
  const { color, secondary, glow } = prop.palette
  if (prop.id === 'magic-wand') {
    return (
      <group position={[r * 1.46, -r * 0.1, r * 0.24]} rotation={[0, 0, -0.4]}>
        <mesh>
          <cylinderGeometry args={[r * 0.05, r * 0.05, r * 0.7, 8]} />
          <meshStandardMaterial color={color} roughness={0.6} />
        </mesh>
        <mesh position={[0, r * 0.45, 0]}>
          <icosahedronGeometry args={[r * 0.16, 0]} />
          <meshStandardMaterial color={glow} emissive={glow} emissiveIntensity={0.9} />
        </mesh>
      </group>
    )
  }
  if (prop.id === 'golden-trophy') {
    return (
      <group position={[r * 1.42, -r * 0.16, r * 0.22]}>
        <mesh position={[0, r * 0.2, 0]}>
          <cylinderGeometry args={[r * 0.22, r * 0.14, r * 0.3, 12]} />
          <meshStandardMaterial color={color} metalness={0.7} roughness={0.25} emissive={glow} emissiveIntensity={0.2} />
        </mesh>
        <mesh position={[0, -r * 0.05, 0]}>
          <cylinderGeometry args={[r * 0.05, r * 0.12, r * 0.24, 12]} />
          <meshStandardMaterial color={color} metalness={0.7} roughness={0.25} />
        </mesh>
      </group>
    )
  }
  return (
    <mesh position={[r * 1.4, -r * 0.1, r * 0.2]}>
      <boxGeometry args={[r * 0.28, r * 0.28, r * 0.18]} />
      <meshStandardMaterial color={color} emissive={secondary} emissiveIntensity={0.15} roughness={0.5} />
    </mesh>
  )
}

function BackProp({ prop, r }: { prop: PropDef; r: number }) {
  const { color, secondary, glow } = prop.palette
  if (prop.id === 'jetpack') {
    return (
      <group position={[0, -r * 0.02, -r * 0.08]}>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * r * 0.28, 0, 0]}>
            <cylinderGeometry args={[r * 0.18, r * 0.2, r * 0.7, 12]} />
            <meshStandardMaterial color={color} metalness={0.4} roughness={0.4} />
          </mesh>
        ))}
        {[-1, 1].map((s) => (
          <mesh key={`t${s}`} position={[s * r * 0.28, -r * 0.5, 0]}>
            <coneGeometry args={[r * 0.14, r * 0.3, 10]} />
            <meshStandardMaterial color={glow} emissive={glow} emissiveIntensity={0.9} />
          </mesh>
        ))}
      </group>
    )
  }
  if (prop.id === 'dragon-wings') {
    return (
      <group position={[0, r * 0.08, -r * 0.08]}>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * r * 1.05, 0, 0]} rotation={[0, s * 0.28, s * 0.3]}>
            <boxGeometry args={[r * 1.1, r * 1.25, r * 0.06]} />
            <meshStandardMaterial color={color} emissive={glow} emissiveIntensity={0.15} roughness={0.6} side={THREE.DoubleSide} />
          </mesh>
        ))}
      </group>
    )
  }
  return (
    <mesh position={[0, r * 0.08, -r * 0.08]}>
      <boxGeometry args={[r * 0.7, r * 0.9, r * 0.14]} />
      <meshStandardMaterial color={color} emissive={secondary} emissiveIntensity={0.2} roughness={0.5} />
    </mesh>
  )
}

function AccessoryProp({ prop, r }: { prop: PropDef; r: number }) {
  const { color, secondary, glow } = prop.palette
  const ref = useRef<THREE.Mesh>(null)
  useFrame((state) => {
    if (ref.current) ref.current.rotation.y = state.clock.elapsedTime * 1.4
  })
  if (prop.id === 'speed-sneakers') {
    return (
      <group position={[0, -r * 1.42, r * 0.18]}>
        {[-1, 1].map((s) => (
          <mesh key={s} position={[s * r * 0.33, 0, 0]}>
            <boxGeometry args={[r * 0.3, r * 0.18, r * 0.5]} />
            <meshStandardMaterial color={color} emissive={secondary} emissiveIntensity={0.3} roughness={0.5} />
          </mesh>
        ))}
      </group>
    )
  }
  return (
    <mesh ref={ref} position={[0, -r * 1.22, r * 0.22]}>
      <icosahedronGeometry args={[r * 0.2, 0]} />
      <meshStandardMaterial color={color} emissive={glow} emissiveIntensity={0.6} roughness={0.3} metalness={0.3} />
    </mesh>
  )
}

function PropModel({ prop, r }: { prop: PropDef; r: number }) {
  switch (prop.slot) {
    case 'head':
      return <HeadProp prop={prop} r={r} />
    case 'hair':
      return <HairProp prop={prop} r={r} />
    case 'face':
      return <FaceProp prop={prop} r={r} />
    case 'hand':
      return <HandProp prop={prop} r={r} />
    case 'back':
      return <BackProp prop={prop} r={r} />
    case 'accessory':
      return <AccessoryProp prop={prop} r={r} />
    default:
      return null
  }
}

export function EmberProps3D({ equippedProps, bodyScale = 1, slots, depth = 0 }: EmberProps3DProps) {
  const r = 0.34 * Math.max(0.92, Math.min(1.12, 1 + (bodyScale - 1) * 0.16))
  const slotSet = slots ? new Set(slots) : null
  const props = equippedProps
    .map((id) => getProp(id))
    .filter((p): p is PropDef => p != null && (!slotSet || slotSet.has(p.slot)))
    .sort((a, b) => SLOT_ORDER.indexOf(a.slot) - SLOT_ORDER.indexOf(b.slot))

  if (props.length === 0) return null
  return (
    <group position={[0, 0, depth]}>
      {props.map((p) => (
        <PropModel key={p.id} prop={p} r={r} />
      ))}
    </group>
  )
}
